import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAxios } from 'shared/hooks/useAxios';
import { ITalentMembersResponse, ITalentProfileIdsResponse, ITalentProfilesResponse } from 'shared/interfaces/IProfile';
import {
  ICreateGuardianPayload,
  ICreateGuardianResponse,
  ICreateTalentPayload,
  ICreateTalentResponse,
  IMessagePayload,
  IProfessionsResponsePayload,
  ISenderInfoResponsePayload,
  ISpecificTalentResponsePayload,
  ITalentBiographyResponsePayload,
  ITalentCalendarEventAggregatesPayload,
  ITalentCalendarEventAggregatesResponse,
  ITalentCalendarEventPayload,
  ITalentCalendarEventsPayload,
  ITalentCalendarEventsResponse,
  ITalentGeneralResponsePayload,
  ITalentMeResponsePayload,
  ITalentResumeResponsePayload,
  ITalentSkillResponsePayload,
  ITalentStatisticsResponsePayload,
  ITalentUpdatePayload,
} from 'shared/interfaces/ITalent';
import { IImpersonateResponsePayload } from 'shared/interfaces/IUser';
import { GET_TALENT_PROFILES, GET_TALENT_PROFILE_IDS } from 'shared/queries/profile.queries';
import { GET_TALENT_MEMBERS, MY_DATA, TALENT_CALENDAR_EVENTS } from 'shared/queries/talent.queries';
import { authToken } from 'shared/utils/authToken';

const { GET, PATCH, POST, DELETE } = useAxios();
export const talentDao = () => {
  const { getAuthToken, getGuardianToken } = authToken();
  const updateTalent = async (payload: ITalentUpdatePayload) => {
    const response = await PATCH({
      url: `${ENDPOINTS.TALENTS}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response?.data;
  };

  const updateMe = async (payload: ITalentUpdatePayload) => {
    const response = await PATCH({
      url: `${ENDPOINTS.ME}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const impersonateTalent = async (userId: string) => {
    const response = await POST<IImpersonateResponsePayload>({
      url: `${ENDPOINTS.IMPERSONATE}/${userId}/impersonate_as_guardian`,
      headers: {
        Authorization: `Bearer ${getGuardianToken()}`,
      },
    });

    return response.data;
  };

  const createGuardian = async (payload: ICreateGuardianPayload) => {
    const response = await POST<ICreateGuardianResponse>({
      url: `${ENDPOINTS.GUARDIAN}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const createTalent = async (payload: ICreateTalentPayload) => {
    const response = await POST<ICreateTalentResponse>({
      url: `${ENDPOINTS.TALENTS}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getStatistics = async () => {
    const response = await GET<ITalentStatisticsResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/statistics`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getResume = async () => {
    const response = await GET<ITalentResumeResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/resume`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getSkills = async () => {
    const response = await GET<ITalentSkillResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/skills`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    const skillData = response.data.data.attributes.skills;

    const savedSkills = await POST({
      url: `https://us-central1-talent-web-dev.cloudfunctions.net/api/skills`,
      ...(skillData && skillData.length > 0 && typeof skillData !== 'string' ? { data: skillData } : { data: [] }),
    });

    const updatedResponse = {
      ...response.data,
      data: {
        ...response.data.data,
        attributes: {
          ...response.data.data.attributes,
          skills: savedSkills.data,
        },
      },
    } as ITalentSkillResponsePayload;

    return updatedResponse;
  };

  const getGeneral = async () => {
    const response = await GET<ITalentGeneralResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/general`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getMe = async () => {
    const response = await GET<ITalentMeResponsePayload>({
      url: `${ENDPOINTS.ME}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getTalentMembers = async (searchString: string, token: string) => {
    const search = () => {
      if (searchString) {
        return { search: searchString };
      } else {
        return {};
      }
    };
    const response = await POST<ITalentMembersResponse>({
      url: ENDPOINTS.GRAPHQL,
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
      data: {
        query: GET_TALENT_MEMBERS,
        // variables: { search: searchString },
        variables: search(),
      },
    });

    return response?.data;
  };

  const getTalentProfiles = async (searchString: string) => {
    const response = await POST<ITalentProfilesResponse>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: GET_TALENT_PROFILES,
        variables: { search: searchString },
      },
    });

    return response.data;
  };

  const getTalentProfileIds = async () => {
    const response = await POST<ITalentProfileIdsResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: GET_TALENT_PROFILE_IDS,
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getBiography = async () => {
    const response = await GET<ITalentBiographyResponsePayload>({
      url: `${ENDPOINTS.TALENTS}/biography`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getProfessions = async (searchString?: string) => {
    const response = await GET<IProfessionsResponsePayload>({
      url: `${ENDPOINTS.PROFESSION}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: {
        search: searchString,
      },
    });
    return response.data;
  };

  const getSenderDetails = async (id: string, type: 'Talent' | 'Company') => {
    const response = await GET<ISenderInfoResponsePayload>({
      // url: `${ENDPOINTS.PROFESSION}?type=${type}&id=${id}`,
      url: `${ENDPOINTS.SENDER_INFO}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: {
        type,
        id,
      },
    });
    return response.data;
  };

  const sendMessage = async (payload: IMessagePayload) => {
    const response = await POST<{ message: string }>({
      url: `${ENDPOINTS.FIRESTORE}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });
    return response.data;
  };

  const getMyData = async (talentId: string) => {
    const response = await POST<ISpecificTalentResponsePayload>({
      url: ENDPOINTS.GRAPHQL,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: MY_DATA,
        variables: {
          id: talentId,
        },
      },
    });

    return response.data;
  };

  const getCalendarAggregates = async (payload: ITalentCalendarEventAggregatesPayload) => {
    const response = await GET<ITalentCalendarEventAggregatesResponse>({
      url: `${ENDPOINTS.CALENDAR_EVENT_AGGREGATES}?date_from=${payload.date_from}&date_to=${payload.date_to}&talent_id=${payload.talent_id}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getTalentCalendarEvents = async (payload: ITalentCalendarEventsPayload, query?: string) => {
    const response = await POST<ITalentCalendarEventsResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: query || TALENT_CALENDAR_EVENTS,
        variables: {
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (response.data.errors) {
      throw new Error('Error' + response.data.errors);
    }

    return response.data;
  };

  const createCalendarEvent = async (payload: ITalentCalendarEventPayload) => {
    const response = await POST<{ message: string }>({
      url: ENDPOINTS.CALENDAR_EVENTS,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const updateCalendarEvent = async (payload: ITalentCalendarEventPayload) => {
    const response = await PATCH<{ message: string }>({
      url: `${ENDPOINTS.CALENDAR_EVENTS}/${payload.id}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: payload,
    });

    return response.data;
  };

  const deleteCalendarEvent = async (calendarEventId: string, talentId: string) => {
    const response = await DELETE<{ message: string }>({
      url: `${ENDPOINTS.CALENDAR_EVENTS}/${calendarEventId}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        talent_id: talentId,
      },
    });

    return response.data;
  };

  return {
    sendMessage,
    getTalentProfileIds,
    updateMe,
    impersonateTalent,
    getTalentProfiles,
    getTalentMembers,
    getMe,
    createTalent,
    createGuardian,
    getSkills,
    getResume,
    updateTalent,
    getStatistics,
    getBiography,
    getProfessions,
    getGeneral,
    getMyData,
    getSenderDetails,
    getCalendarAggregates,
    getTalentCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
  };
};
